import { useState } from 'react';
import { Car } from '../../types/Car';

interface Props {
  car: Car;
}

export default function PriceSidebar({ car }: Props) {
  const [deposit, setDeposit] = useState(50000);
  const [term, setTerm]       = useState(48);
  const [rate, setRate]       = useState(9.9);
  const phoneNumber = "919766624663"; // format: country code + number (no +)

  // ── normalise price to a clean number regardless of what Firestore returns
  const price = (() => {
    const raw = car.veh_price;
    if (typeof raw === 'number' && !isNaN(raw)) return raw;
    const parsed = Number(String(raw).replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  })();

  const principal   = Math.max(0, price - deposit);
  const monthlyRate = rate / (100 * 12);
  const monthlyPayment =
    monthlyRate === 0
      ? principal / term
      : (principal * (monthlyRate * Math.pow(1 + monthlyRate, term))) /
        (Math.pow(1 + monthlyRate, term) - 1);

  const fmt = (n: number) => Math.round(n).toLocaleString('en-IN');

  return (
    <div style={wrap}>

      {/* ── PRICE CARD ── */}
      <div style={card}>

        {/* Asking price */}
        <div style={priceLabel}>ASKING PRICE</div>
        <div style={priceValue}>₹ {fmt(price)}</div>
        <div style={priceSub}>On Road Price &nbsp;·&nbsp; VAT Qualifying</div>

        <div style={divider} />

        <div style={ctaStack}>
          <button
                style={buyBtn}
                onClick={() => {
                    const phone = "919766624663";

                const message = `Hi, I want a test drive of this vehicle:
                ${car.name} (${car.year}) (${car.fuel}) (${car.transmission})
                Price: ₹ ${Number(car.veh_price).toLocaleString('en-IN')}`;

                    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
                    window.open(url, "_blank");
                }}
                >
                Book a Test Drive
                </button>
        </div>

        <div style={divider} />

        {/* EMI calculator */}
        <div style={emiBox}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={emiLabel}>Monthly EMI</div>
              <div style={emiValue}>₹ {fmt(monthlyPayment)}</div>
            </div>
            <div style={emiMeta}>{term} mo · {rate}% APR</div>
          </div>
        </div>

        {/* Sliders */}


        <div style={sliderSection}>
          <div style={sliderRow}>
            <span style={sliderLabel}>Down Payment</span>
            <span style={sliderValue}>₹ {fmt(deposit)}</span>
          </div>
          <input
            type="range"
            min={0}
            max={Math.round(price * 0.5)}
            step={5000}
            value={deposit}
            onChange={e => setDeposit(Number(e.target.value))}
            style={slider}
          />

          <div style={{ ...sliderRow, marginTop: '14px' }}>
            <span style={sliderLabel}>Loan Term</span>
            <span style={sliderValue}>{term} months</span>
          </div>
          <input
            type="range" min={12} max={84} step={12}
            value={term} onChange={e => setTerm(Number(e.target.value))}
            style={slider}
          />

          <div style={{ ...sliderRow, marginTop: '14px' }}>
            <span style={sliderLabel}>Interest Rate</span>
            <span style={sliderValue}>{rate}% APR</span>
          </div>
          <input
            type="range" min={5} max={20} step={0.1}
            value={rate} onChange={e => setRate(Number(e.target.value))}
            style={slider}
          />
        </div>


        {/* CTAs */}

      </div>

      {/* ── CONTACT CARD ── */}
      <div style={card}>
        <div style={contactTitle}>Contact Dealer</div>

        <div style={contactGrid}>
            <button
                style={contactBtn}
                onClick={() => window.location.href = `tel:+${phoneNumber}`}
            >
                📞 &nbsp;Call
            </button>

            <button
                style={contactBtn}
                onClick={() => window.open(`https://wa.me/${phoneNumber}`, '_blank')}
            >
                💬 &nbsp;WhatsApp
            </button>
            </div>

        {/* Quick stats */}

      
      </div>

      <style>{`
        input[type=range] {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          border-radius: 2px;
          background: #2a2a2a;
          outline: none;
          cursor: pointer;
        }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #FFD700;
          cursor: pointer;
          border: 2px solid #080808;
          box-shadow: 0 0 0 2px #FFD70044;
        }
        input[type=range]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #FFD700;
          cursor: pointer;
          border: 2px solid #080808;
        }
      `}</style>
    </div>
  );
}

/* ── HELPERS ── */
function infoCell(label: string, value: string) {
  return (
    <div style={infoCellWrap}>
      <div style={infoCellLabel}>{label}</div>
      <div style={infoCellValue}>{value}</div>
    </div>
  );
}

/* ── STYLES ── */
const wrap: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '16px', margin:'5px',
};

const card: React.CSSProperties = {
  background: '#111', border: '1px solid #1e1e1e',
  borderRadius: '16px', padding: '20px',
  margin:'5px'
};

const priceLabel: React.CSSProperties = {
  fontSize: '10px', color: '#444', letterSpacing: '0.1em',
  textTransform: 'uppercase', marginBottom: '6px',
};
const priceValue: React.CSSProperties = {
  fontSize: '28px', fontWeight: 800, color: '#FFD700',
  letterSpacing: '-0.02em', lineHeight: 1,
};
const priceSub: React.CSSProperties = {
  fontSize: '11px', color: '#444', marginTop: '6px',
};

const divider: React.CSSProperties = {
  height: '1px', background: '#1a1a1a', margin: '16px 0',
};

const emiBox: React.CSSProperties = {
  background: '#141414', border: '1px solid #1e1e1e',
  borderRadius: '10px', padding: '14px', marginBottom: '16px',
};
const emiLabel: React.CSSProperties = {
  fontSize: '10px', color: '#555', letterSpacing: '0.06em',
  textTransform: 'uppercase', marginBottom: '4px',
};
const emiValue: React.CSSProperties = {
  fontSize: '22px', fontWeight: 700, color: '#fff',
};
const emiMeta: React.CSSProperties = {
  fontSize: '11px', color: '#444', textAlign: 'right',
  lineHeight: 1.5,
};

const sliderSection: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };
const sliderRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const sliderLabel: React.CSSProperties = { fontSize: '12px', color: '#555' };
const sliderValue: React.CSSProperties = { fontSize: '12px', color: '#ccc', fontWeight: 600 };
const slider: React.CSSProperties = { width: '100%', marginTop: '4px' };

const ctaStack: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '10px' };
const buyBtn: React.CSSProperties = {
  width: '100%', padding: '13px', fontSize: '14px', fontWeight: 700,
  borderRadius: '10px', border: 'none', background: '#FFD700',
  color: '#000', cursor: 'pointer',
};
const testBtn: React.CSSProperties = {
  width: '100%', padding: '13px', fontSize: '14px', fontWeight: 500,
  borderRadius: '10px', border: '1px solid #252525',
  background: '#161616', color: '#aaa', cursor: 'pointer',
};

const contactTitle: React.CSSProperties = {
  fontSize: '14px', fontWeight: 600, color: '#ccc' ,marginBottom: '12px' ,
};
const contactGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
};
const contactBtn: React.CSSProperties = {
  padding: '12px', borderRadius: '10px', border: '1px solid #222',
  background: '#161616', color: '#bbb', fontSize: '13px',
  cursor: 'pointer', fontWeight: 500,
};

const infoGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
};
const infoCellWrap: React.CSSProperties = {
  background: '#141414', border: '1px solid #1a1a1a',
  borderRadius: '8px', padding: '8px 10px',
};
const infoCellLabel: React.CSSProperties = {
  fontSize: '9px', color: '#444', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: '3px',
};
const infoCellValue: React.CSSProperties = {
  fontSize: '12px', color: '#ccc', fontWeight: 500,
};